import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Icon, Popup } from "semantic-ui-react";
import {
  changeEditMode,
  changeSelectedSigner,
  setPendingSignaturePlacement,
} from "../../../../../app/annots/annotSlice";
import { openModal } from "../../../../../app/common/modals/modalSlice";
import { roleIsOtherAgentsClients } from "../sendForSigning/sendForSigningUtils";
import DocPrepareSignerListItem from "./DocPrepareSignerListItem";
import { useMediaQuery } from "react-responsive";
import { toast } from "react-toastify";
import { getFormFieldData } from "../../docFill/formFieldData/formFieldData";
import {
  getSignatureRecipientProfile,
  notifySignerToSign,
} from "./signatureNotificationUtils";
import { onSnapshot } from "firebase/firestore";
import {
  dataFromSnapshot,
  fetchNotificationsForTransactionFromDb,
} from "../../../../../app/firestore/firestoreService";

const ADMIN_ROLES = ["tc", "assistant", "manager", "managerassistant"];

export default function DocPrepareSignerList() {
  const dispatch = useDispatch();
  const { signerListDisplay } = useSelector(
    (state) => state.annot
  );
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { editMode } = useSelector((state) => state.annot);
  const { transaction, parties } = useSelector((state) => state.transaction);
  const { doc } = useSelector((state) => state.doc);
  const isMobile = useMediaQuery({ query: "(max-width:768px)" });
  const [notifyingAgent, setNotifyingAgent] = useState(false);
  const [notifyingCoAgent, setNotifyingCoAgent] = useState(false);
  const [signatureNotifications, setSignatureNotifications] = useState([]);
  const transactionId = transaction?.id;
  const docId = doc?.id;

  // Determine if the current user is an admin role
  const isAdminRole = ADMIN_ROLES.includes(currentUserProfile?.role);
  const currentUserId = currentUserProfile?.userId || currentUserProfile?.id;
  const isTransactionAgent = currentUserId === transaction?.userId;
  const isTransactionCoAgent = currentUserId === transaction?.coAgentId;

  // Determine the broker signature field name based on what side the agent represents
  const signatureFieldName =
    transaction?.agentRepresents === "Buyer"
      ? "Signature Buyer Broker"
      : "Signature Seller Broker";
  const dateFieldName =
    transaction?.agentRepresents === "Buyer"
      ? "Date Signature Buyer Broker"
      : "Date Signature Seller Broker";

  // Check if agent/co-agent have already signed
  const agentHasSigned = !!doc?.formFieldValues?.[signatureFieldName];

  // Co-agent exists if coAgentId is present
  const coAgentExists = !!transaction?.coAgentId;

  const signatureFieldNameCoAgent = transaction?.agentRepresents === "Buyer" 
    ? "Signature Buyer CoAgent"
    : "Signature Seller CoAgent";
  const dateFieldNameCoAgent = transaction?.agentRepresents === "Buyer"
    ? "Date Signature Buyer CoAgent"
    : "Date Signature Seller CoAgent";

  const coAgentHasSigned = !!doc?.formFieldValues?.[signatureFieldNameCoAgent];

  const formFieldData = getFormFieldData(doc?.title, transaction) || [];
  const hasAgentSignatureField = formFieldData.some(
    (field) => field.name === signatureFieldName
  );
  const hasCoAgentSignatureField = formFieldData.some(
    (field) => field.name === signatureFieldNameCoAgent
  );
  const hasAgentSignatureLocation = !!doc?.agentSignatureLocations?.agent;
  const hasCoAgentSignatureLocation = !!doc?.agentSignatureLocations?.coagent;

  useEffect(() => {
    if (!transactionId || !docId) {
      setSignatureNotifications([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      fetchNotificationsForTransactionFromDb(transactionId),
      (snapshot) => {
        const notifications = snapshot.docs
          .map((notificationDoc) => dataFromSnapshot(notificationDoc))
          .filter(
            (notification) =>
              notification?.type === "signatureRequired" &&
              notification?.docId === docId &&
              notification?.status !== "deleted" &&
              ["agent", "coagent"].includes(notification?.signatureRoleKey)
          );
        setSignatureNotifications(notifications);
      },
      (error) => {
        console.log("Error loading signature notifications", error);
        setSignatureNotifications([]);
      }
    );

    return () => unsubscribe();
  }, [transactionId, docId]);

  const agentSignatureNotification = signatureNotifications.find(
    (notification) => notification.signatureRoleKey === "agent"
  );
  const coAgentSignatureNotification = signatureNotifications.find(
    (notification) => notification.signatureRoleKey === "coagent"
  );

  function formatDateTime(dateValue) {
    if (!dateValue) return "date unavailable";
    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
      return date.toLocaleString();
    } catch (error) {
      return "date unavailable";
    }
  }

  function renderSignatureRequestControl({
    hasSigned,
    notification,
    signedText,
    notifiedText,
    button,
  }) {
    if (hasSigned) {
      return (
        <div style={{ color: "#21ba45", fontSize: "0.85em", marginBottom: "6px" }}>
          <Icon name="check circle" /> {signedText}
        </div>
      );
    }

    if (notification) {
      return (
        <div style={{ color: "#2185d0", fontSize: "0.85em", marginBottom: "6px" }}>
          <Icon name="bell" /> {notifiedText} on{" "}
          {formatDateTime(notification.createdAt)}
        </div>
      );
    }

    return button;
  }

  async function requestSignaturePlacement({
    roleKey,
    label,
    recipientId,
    signatureFieldName,
    dateFieldName,
  }) {
    const recipientProfile = await getSignatureRecipientProfile(transaction, roleKey);
    dispatch(
      setPendingSignaturePlacement({
        roleKey,
        label,
        recipientId,
        recipientProfile,
        signatureFieldName,
        dateFieldName,
      })
    );
    toast.info(
      `Click where the ${label} should sign, then the notification will be sent.`
    );
  }

  async function handleNotifyAgent() {
    if (!transaction?.userId) return;
    if (!agentHasSigned && !hasAgentSignatureField && !hasAgentSignatureLocation) {
      await requestSignaturePlacement({
        roleKey: "agent",
        label: "Agent",
        recipientId: transaction.userId,
        signatureFieldName,
        dateFieldName,
      });
      return;
    }
    setNotifyingAgent(true);
    try {
      await notifySignerToSign({
        transaction,
        doc,
        currentUserProfile,
        roleKey: "agent",
      });
      toast.success("Agent notified to sign.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setNotifyingAgent(false);
    }
  }

  async function handleNotifyCoAgent() {
    if (!transaction?.coAgentId) return;
    if (!coAgentHasSigned && !hasCoAgentSignatureField && !hasCoAgentSignatureLocation) {
      await requestSignaturePlacement({
        roleKey: "coagent",
        label: "Co-Agent",
        recipientId: transaction.coAgentId,
        signatureFieldName: signatureFieldNameCoAgent,
        dateFieldName: dateFieldNameCoAgent,
      });
      return;
    }
    setNotifyingCoAgent(true);
    try {
      await notifySignerToSign({
        transaction,
        doc,
        currentUserProfile,
        roleKey: "coagent",
      });
      toast.success("Co-Agent notified to sign.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setNotifyingCoAgent(false);
    }
  }

  function handleAddSigner() {
    dispatch(
      openModal({
        modalType: "DocPrepareAddSigner",
      })
    );
  }

  function handleYourFieldsClick() {
    dispatch(changeSelectedSigner(currentUserProfile));
    dispatch(changeEditMode("agent"));
  }

  const annotColor = "200, 200, 200";

  // Separate current agent's clients from other party clients
  const currentAgentClients = signerListDisplay.filter(
    (signer) => !roleIsOtherAgentsClients(signer.role, transaction)
  );

  // Get other side clients from parties that have names
  // This includes parties that exist in the Parties section with firstName and lastName
  const otherSidePartiesWithNames = parties.filter(
    (party) =>
      roleIsOtherAgentsClients(party.role, transaction) &&
      party.firstName &&
      party.lastName
  );

  // Determine if we should show the other side section:
  // Show if there are other side parties with names in the Parties section
  const showOtherSideSection = otherSidePartiesWithNames.length > 0;

  // Debug logging
  // console.log("DocPrepareSignerList - signerListDisplay:", signerListDisplay);
  // console.log("DocPrepareSignerList - currentAgentClients:", currentAgentClients);
  // console.log("DocPrepareSignerList - otherPartyClients:", otherPartyClients);
  // console.log("DocPrepareSignerList - agentRepresents:", transaction.agentRepresents);

  // Determine the title for other party clients
  const otherPartyTitle =
    transaction.agentRepresents === "Buyer" ? "Seller" : "Buyer";
  const canNotifyAgentToSign = isAdminRole || isTransactionCoAgent;
  const canNotifyCoAgentToSign = isAdminRole || isTransactionAgent;
  const showAgentSignatureRequestSection =
    canNotifyAgentToSign || (coAgentExists && canNotifyCoAgentToSign);

  return (
    <>
      {" "}
      <div
        onClick={() => handleYourFieldsClick()}
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <Icon
          className="small right margin"
          name={
            editMode === "agent" ? "square check outline" : "square outline"
          }
          size="large"
        />
        <div style={{ width: "20px", height: "15px" }}>
          <div
            className="annot-outer-wrapper"
            style={{
              backgroundColor: `rgba(${annotColor}, 0.2)`,
            }}
          >
            <div
              className="annot-inner-wrapper"
              style={{
                boxShadow: `rgb(${annotColor}) 0px 0px 0px 1px`,
                zIndex: 5,
              }}
            ></div>
          </div>
        </div>
        <h5
          className="zero top margin small left margin"
          style={{ color: "#666" }}
        >
          Me:&nbsp;
          {currentUserProfile.firstName + " " + currentUserProfile.lastName}
        </h5>
      </div>
      <br />
      {currentAgentClients.map((client, index) => (
        <DocPrepareSignerListItem
          signer={client}
          key={client.role}
          index={index}
        />
      ))}
      {showOtherSideSection && (
        <>
          <div className="tiny vertical margin">
            <div
              style={{
                color: "#9a9a9a",
                marginBottom: "2px",
                marginTop: "0px",
              }}
            >
              {otherPartyTitle} Clients:{" "}
              {!isMobile && (
                <Popup
                  flowing
                  size="small"
                  trigger={
                    <Icon
                      name="info"
                      color="blue"
                      circular
                      inverted
                      size="tiny"
                      style={{ marginLeft: "3px", marginBottom: "3px" }}
                    />
                  }
                >
                  <p className="bold text blue mini bottom margin">
                    Other Side's Clients
                  </p>
                  <p style={{ margin: "0 0 8px 0" }}>
                    <strong>Note:</strong> You do not share or send for signing
                    directly to the other side's clients.
                    <br />
                    When the other agent logs in (as a TransActioner subscriber
                    or not), <br /> they can easily send these documents out to
                    their clients for signatures.
                  </p>
                  <p style={{ margin: "0" }}>
                    <strong>Dual Agent:</strong>If you are acting as a dual
                    agent, <br />
                    you should set up the other side as a separate transaction,
                    link the two transactions, <br />
                    and then send for signatures to the other side's clients
                    inside that other transaction.
                  </p>
                </Popup>
              )}
            </div>
          </div>
          {/* {otherPartyClients.map((client, index) => ( */}
          {otherSidePartiesWithNames.map((client, index) => (
            <DocPrepareSignerListItem
              signer={client}
              key={client.role}
              index={index}
            />
          ))}
        </>
      )}
      {!showOtherSideSection && (
        <span className="text-small text gray tiny margin">
          <br />
          <i>
            To see the other side's clients signature boxes, add their names
            inside Parties.
          </i>
          <br />
        </span>
      )}
      <Button basic size="tiny" onClick={() => handleAddSigner()}>
        Add Signer
      </Button>

      {showAgentSignatureRequestSection && (
        <div style={{ marginTop: "12px", borderTop: "1px solid #e8e8e8", paddingTop: "10px" }}>
          <div style={{ color: "#9a9a9a", marginBottom: "6px", fontSize: "0.9em" }}>
            Agent Signature Request:
            <Popup
                  flowing
                  size="large"
                  trigger={
                    <Icon
                      name="info"
                      color="blue"
                      circular
                      inverted
                      size="tiny"
                      style={{ marginLeft: "3px", marginBottom: "3px" }}
                    />
                  }
                >
                  <p className="bold text blue mini bottom margin">
                    Request Agent or Co-Agent to Sign
                  </p>
                
                    <p className="text-dark text-normal mini bottom margin">
                      Use these buttons to notify the agent or co-agent that their signature is needed.
                  <br />
                  If the document is a TransActioner form that has a signature field specifically assigned to them, TransActioner will use that field. 
                  <br />If no assigned field exists, you will be prompted to click where their signature should go. 
                  <br />You can then move the signature box into position and click the Save button at the bottom.
                  <br /> <br />
                  The agent or co-agent will receive an email notification, and a notification on their dashboard with a quick link to sign the document. 
                  <br />The email is also saved to the transaction email history.
                    </p>
                  
                </Popup>
          </div>
          {canNotifyAgentToSign &&
            renderSignatureRequestControl({
              hasSigned: agentHasSigned,
              notification: agentSignatureNotification,
              signedText: "Agent has signed",
              notifiedText: "Agent notified to sign",
              button: (
                <Button
                  basic
                  size="tiny"
                  color="blue"
                  loading={notifyingAgent}
                  disabled={notifyingAgent}
                  onClick={handleNotifyAgent}
                  style={{ marginBottom: "6px" }}
                >
                  <Icon name="bell" /> Notify Agent to Sign
                </Button>
              ),
            })}
          {coAgentExists && canNotifyCoAgentToSign && (
            renderSignatureRequestControl({
              hasSigned: coAgentHasSigned,
              notification: coAgentSignatureNotification,
              signedText: "Co-Agent has signed",
              notifiedText: "Co-Agent notified to sign",
              button: (
                <Button
                  basic
                  size="tiny"
                  color="teal"
                  loading={notifyingCoAgent}
                  disabled={notifyingCoAgent}
                  onClick={handleNotifyCoAgent}
                >
                  <Icon name="bell" /> Notify Co-Agent to Sign
                </Button>
              ),
            })
          )}
        </div>
      )}
    </>
  );
}
